/* ============================================================
   Fence Works — send only what changed (cloud save)
   lib/supabase.ts persist() used to upsert EVERY row of every table on every
   save: one edit to one estimate re-sent the workspace's whole customer list,
   every job and every estimate. The save now keeps a ledger per table — row
   id → fingerprint of the row as the database holds it (what this tab last
   wrote, or loaded verbatim) — and upserts only the rows whose fingerprint
   moved, in size-capped slices so a big import never becomes one giant
   request.

   Plain functions, no imports, so scripts/unit.mjs can test them in a bare vm.
   ============================================================ */

/* 64-bit string hash (cyrb53's two 32-bit lanes, both kept), prefixed with the
   length. A fingerprint only has to tell "same row" from "edited row"; it is
   never a security boundary. */
function fpHash(str: string): string {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return str.length.toString(36) + "." + (h2 >>> 0).toString(36) + "." + (h1 >>> 0).toString(36);
}

/* The row as it is compared: without what the write stamps rather than what
   the operator changed — created_at / updated_at (a quote or project with no
   createdAt maps to "now" on every save) and data._rev (the quote row version
   the database bumps on every write; it moves after each save while the quote
   itself hasn't). Returns the JSON so the caller can also size the row. */
function rowJson(row: any): string {
  if (!row || typeof row !== "object") return JSON.stringify(row === undefined ? null : row);
  const r: any = Object.assign({}, row);
  delete r.created_at; delete r.updated_at;
  if (r.data && typeof r.data === "object" && "_rev" in r.data) { r.data = Object.assign({}, r.data); delete r.data._rev; }
  return JSON.stringify(r);
}

function rowFingerprint(row: any): string {
  return fpHash(rowJson(row));
}

/* Which mapped rows need sending, against book (Map: row id → fingerprint).
   → { changed, fps: { id → fingerprint to record once it lands }, bytes: { id → approx JSON size } }
   A row with no id can't be tracked, so it is always sent (and the database
   reports the problem, as it always did). */
function changedRows(rows: any[], book: Map<string, string> | null) {
  const changed: any[] = [];
  const fps: Record<string, string> = {};
  const bytes: Record<string, number> = {};
  (rows || []).forEach((r) => {
    const id = r && r.id != null && r.id !== "" ? String(r.id) : null;
    if (!id) { changed.push(r); return; }
    const json = rowJson(r);
    const fp = fpHash(json);
    if (book && book.get(id) === fp) return;
    changed.push(r);
    fps[id] = fp;
    bytes[id] = json.length;
  });
  return { changed, fps, bytes };
}

/* Cut rows into upsert requests of at most maxRows rows and ~maxBytes of JSON
   (a single bigger row still goes, alone). Keeps order. */
function upsertSlices(rows: any[], bytes: Record<string, number>, maxRows: number, maxBytes: number): any[][] {
  const out: any[][] = [];
  let cur: any[] = [], size = 0;
  (rows || []).forEach((r) => {
    const b = (r && r.id != null && bytes && bytes[String(r.id)]) || 0;
    if (cur.length && (cur.length >= maxRows || size + b > maxBytes)) { out.push(cur); cur = []; size = 0; }
    cur.push(r); size += b;
  });
  if (cur.length) out.push(cur);
  return out;
}

/* ids this tab may delete: on its seen ledger and gone from the current rows.
   No ledger (the table was never hydrated here) → nothing is provably deleted. */
function pruneIds(seen: Set<string> | null | undefined, currentIds: string[]): string[] {
  if (!seen) return [];
  const current = new Set((currentIds || []).map(String));
  return Array.from(seen).filter((id) => !current.has(String(id)));
}

/* an id list as one order-insensitive comparable key */
function idSetKey(ids: any[]): string {
  return (ids || []).map(String).sort().join("\n");
}

export { fpHash, rowJson, rowFingerprint, changedRows, upsertSlices, pruneIds, idSetKey };
